import { availableFiltersList } from "src/organisms/AlgoliaProductList/availableFilters";
import {
  BRAND_FACET,
  CATEGORIES_FACET,
  COLOUR_FACET,
  DISCOUNT_FACET,
  PATTERN_FACET,
  SIZE_FACET,
  CONSCIOUS_FACET,
  STYLE_FACET,
  PRICE_FACET,
} from "../utils/algolia";
import {
  RangeMax,
  RangeMin,
} from "instantsearch.js/es/connectors/range/connectRange";
import { useCallback } from "react";
import { useRouter } from "next/router";

export const queryStringKeyMap: Record<string, string> = {
  [CATEGORIES_FACET]: "cat_slug",
  [BRAND_FACET]: "brand_slug",
  [COLOUR_FACET]: "colors",
  [PATTERN_FACET]: "patterns",
  [SIZE_FACET]: "sizes",
  [DISCOUNT_FACET]: "discount",
  [CONSCIOUS_FACET]: "goodOnYouScore",
  [STYLE_FACET]: "styles",
} as const;

export const queryStringReverseKeyMap: Record<string, string> = {
  [queryStringKeyMap[CATEGORIES_FACET]]: CATEGORIES_FACET,
  [queryStringKeyMap[BRAND_FACET]]: BRAND_FACET,
  [queryStringKeyMap[COLOUR_FACET]]: COLOUR_FACET,
  [queryStringKeyMap[PATTERN_FACET]]: PATTERN_FACET,
  [queryStringKeyMap[SIZE_FACET]]: SIZE_FACET,
  [queryStringKeyMap[DISCOUNT_FACET]]: DISCOUNT_FACET,
  [queryStringKeyMap[CONSCIOUS_FACET]]: CONSCIOUS_FACET,
  [queryStringKeyMap[STYLE_FACET]]: STYLE_FACET,
} as const;

export const queryStringValuePosMap: Record<string, number> = {
  [CATEGORIES_FACET]: -1,
  [BRAND_FACET]: 2,
  [COLOUR_FACET]: 0,
  [PATTERN_FACET]: 0,
  [SIZE_FACET]: 0,
  [DISCOUNT_FACET]: 0,
  [CONSCIOUS_FACET]: 0,
  [STYLE_FACET]: 0,
} as const;

export const queryStringValuePosReverseMap: Record<string, number> = {
  [queryStringKeyMap[CATEGORIES_FACET]]: 2,
  [queryStringKeyMap[BRAND_FACET]]: 2,
  [queryStringKeyMap[COLOUR_FACET]]: 0,
  [queryStringKeyMap[PATTERN_FACET]]: 0,
  [queryStringKeyMap[SIZE_FACET]]: 0,
  [queryStringKeyMap[DISCOUNT_FACET]]: 0,
  [queryStringKeyMap[CONSCIOUS_FACET]]: 0,
  [queryStringKeyMap[STYLE_FACET]]: 0,
} as const;

export function useHandleSearchParams(): {
  handleSearchParams: (props: {
    facet: string;
    value: string;
    values?: string[];
    isSingle?: boolean;
    shouldDelete?: boolean;
    deletePage?: boolean;
  }) => void;
  handleSearchParamsPrice: ({
    min,
    max,
  }: {
    min?: RangeMin;
    max?: RangeMax;
  }) => void;
} {
  const router = useRouter();

  const handleSearchParams = useCallback(
    ({
      facet,
      value,
      values,
      isSingle,
      shouldDelete = false,
      deletePage = false,
    }: {
      facet: string;
      value: string;
      values?: string[];
      isSingle?: boolean;
      shouldDelete?: boolean;
      deletePage?: boolean;
    }) => {
      const url = new URL(location.toString());
      const paramValue =
        value.split("||").at(queryStringValuePosMap[facet]) || "";
      const selected = url.searchParams.has(
        queryStringKeyMap[facet] || facet,
        paramValue
      );

      if (shouldDelete) {
        url.searchParams.delete(queryStringKeyMap[facet] || facet, paramValue);

        void router.push(url.toString(), undefined, {
          scroll: false,
          shallow: true,
        });

        return;
      }

      if (selected) {
        if (values) {
          values.forEach((val) => {
            url.searchParams.delete(queryStringKeyMap[facet] || facet, val);
          });
        } else {
          url.searchParams.delete(
            queryStringKeyMap[facet] || facet,
            paramValue
          );
        }
      }

      if (!selected && !isSingle) {
        if (values) {
          values.forEach((val) => {
            url.searchParams.append(queryStringKeyMap[facet] || facet, val);
          });
        } else {
          url.searchParams.append(
            queryStringKeyMap[facet] || facet,
            paramValue
          );
        }
      }

      if (!selected && isSingle && !Array.isArray(paramValue)) {
        url.searchParams.set(queryStringKeyMap[facet] || facet, paramValue);
      }

      if (deletePage && url.searchParams.has("page")) {
        url.searchParams.delete("page");

        void router.replace(url.toString(), undefined, {
          scroll: false,
          shallow: true,
        });

        return;
      }

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });
    },
    [router]
  );

  const handleSearchParamsPrice = useCallback(
    ({ min, max }: { min?: RangeMin; max?: RangeMax }) => {
      const url = new URL(location.toString());

      if (min) {
        url.searchParams.set("min_price", `${min}`);
      }

      if (max) {
        url.searchParams.set("max_price", `${max}`);
      }

      if (url.searchParams.has("page")) {
        url.searchParams.delete("page");

        void router.replace(url.toString(), undefined, {
          scroll: false,
          shallow: true,
        });

        return;
      }

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });
    },
    [router]
  );

  return { handleSearchParams, handleSearchParamsPrice };
}

export function useRemoveFilters(): {
  removeAllFiltersByKey: (
    facet: string,
    value?: string,
    deletePage?: boolean
  ) => void;
  removeAllFilters: (removeCategories?: boolean) => void;
  removePriceFilters: () => void;
} {
  const router = useRouter();

  const removeAllFilters = useCallback(
    (removeCategories?: boolean) => {
      const url = new URL(location.toString());

      availableFiltersList
        .filter(
          ({ facet }) => facet !== PRICE_FACET && facet !== CATEGORIES_FACET
        )
        .forEach(({ facet }) => {
          url.searchParams.delete(queryStringKeyMap[facet]);
        });

      url.searchParams.delete("min_price");
      url.searchParams.delete("max_price");
      url.searchParams.delete("page");

      if (removeCategories) {
        url.searchParams.delete("cat_slug");
      }

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });
    },
    [router]
  );

  const removePriceFilters = useCallback(() => {
    const url = new URL(location.toString());

    url.searchParams.delete("min_price");
    url.searchParams.delete("max_price");

    void router.push(url.toString(), undefined, {
      scroll: false,
      shallow: true,
    });
  }, [router]);

  const removeAllFiltersByKey = useCallback(
    (facet: string, value?: string, deletePage?: boolean) => {
      const url = new URL(location.toString());

      if (!url.searchParams.has(queryStringKeyMap[facet] || facet)) {
        return;
      }

      if (value) {
        url.searchParams.delete(queryStringKeyMap[facet] || facet, value);
      } else {
        url.searchParams.delete(queryStringKeyMap[facet] || facet);
      }

      if (deletePage && url.searchParams.has("page")) {
        url.searchParams.delete("page");

        void router.replace(url.toString(), undefined, {
          scroll: false,
          shallow: true,
        });

        return;
      }

      void router.push(url.toString(), undefined, {
        scroll: false,
        shallow: true,
      });
    },
    [router]
  );

  return { removeAllFilters, removePriceFilters, removeAllFiltersByKey };
}
